import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Person, Photo } from '../types';
import { PersonService } from './personService';
import PhotoService from './photoService';
import { ChangeTrackingService } from './changeTrackingService';
import { Change } from '../types/change';

/**
 * QuickStats Interface
 * Dashboard statistics summary
 */
export interface QuickStats {
  totalPeople: number;
  totalRelationships: number;
  totalPhotos: number;
  generations: number;
}

/**
 * Activity Interface
 * Recent activity item for dashboard feed
 */
export interface Activity {
  id: string;
  userId: string;
  userName: string;
  action: string;
  description: string;
  timestamp: Date;
  link?: string;
  icon?: string;
}

/**
 * DashboardService
 *
 * Aggregates data for dashboard display using existing services.
 * This is a lightweight service that composes data from multiple sources.
 *
 * NO NEW BACKEND WORK - Uses only existing services!
 */
export class DashboardService {
  /**
   * Get quick statistics for dashboard
   *
   * @returns {Promise<QuickStats>} Dashboard statistics
   * @throws {Error} If data fetch fails
   */
  static async getQuickStats(): Promise<QuickStats> {
    try {
      // Fetch counts and sample persons in parallel using PersonService
      const [peopleCount, relationshipsCount, photosCount, samplePersons] = await Promise.all([
        PersonService.getPersonCount(),
        this.countRelationships(),
        this.countPhotos(),
        PersonService.getPersonsWithBirthDates(1000),
      ]);

      // Calculate generations from sample
      const generations = this.calculateGenerations(samplePersons);

      return {
        totalPeople: peopleCount,
        totalRelationships: relationshipsCount,
        totalPhotos: photosCount,
        generations,
      };
    } catch (error: any) {
      console.error('Failed to get quick stats:', error);
      throw new Error(`Failed to get dashboard statistics: ${error.message}`);
    }
  }

  /**
   * Get recent activity for dashboard feed
   *
   * @param {number} limitCount - Maximum number of activities to return
   * @returns {Promise<Activity[]>} Array of recent activities
   * @throws {Error} If data fetch fails
   */
  static async getRecentActivity(limitCount: number = 10): Promise<Activity[]> {
    try {
      // Use ChangeTrackingService to get recent approved changes
      const recentChanges = await ChangeTrackingService.getFilteredChanges(
        { status: 'approved' },
        limitCount
      );

      // Transform changes to activity items
      const activities: Activity[] = recentChanges.map((change: Change) => {
        const activity: Activity = {
          id: change.id,
          userId: change.userId,
          userName: change.userName,
          action: this.getActionText(change),
          description: this.getActionDescription(change),
          timestamp: new Date(change.createdAt),
          link: this.getActionLink(change),
          icon: this.getActionIcon(change),
        };
        return activity;
      });

      return activities;
    } catch (error: any) {
      console.error('Failed to get recent activity:', error);
      // Return empty array on error to prevent dashboard from breaking
      return [];
    }
  }

  /**
   * Get incomplete profiles that need attention
   *
   * @param {number} limitCount - Maximum number of profiles to return
   * @returns {Promise<Person[]>} Array of incomplete persons
   * @throws {Error} If data fetch fails
   */
  static async getIncompleteProfiles(limitCount: number = 5): Promise<Person[]> {
    try {
      // Use PersonService to get incomplete profiles
      return await PersonService.getIncompleteProfiles(limitCount);
    } catch (error: any) {
      console.error('Failed to get incomplete profiles:', error);
      return [];
    }
  }

  /**
   * Get upcoming birthdays in next N days
   *
   * @param {number} days - Number of days to look ahead
   * @returns {Promise<Person[]>} Array of persons with upcoming birthdays
   * @throws {Error} If data fetch fails
   */
  static async getUpcomingBirthdays(days: number = 30): Promise<Person[]> {
    try {
      // Use PersonService to get upcoming birthdays
      return await PersonService.getUpcomingBirthdays(days);
    } catch (error: any) {
      console.error('Failed to get upcoming birthdays:', error);
      return [];
    }
  }

  /**
   * Get recently uploaded photos
   *
   * @param {number} limitCount - Maximum number of photos to return
   * @returns {Promise<Photo[]>} Array of recent photos
   * @throws {Error} If data fetch fails
   */
  static async getRecentPhotos(limitCount: number = 5): Promise<Photo[]> {
    try {
      // Query photos collection directly for most recent
      const photosRef = collection(db, 'photos');
      const q = query(
        photosRef,
        where('isDeleted', '!=', true),
        orderBy('isDeleted'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const photos: Photo[] = [];

      querySnapshot.forEach(doc => {
        photos.push({ id: doc.id, ...doc.data() } as Photo);
      });

      return photos;
    } catch (error: any) {
      console.error('Failed to get recent photos:', error);
      return [];
    }
  }

  /**
   * Count total relationships in database
   *
   * @returns {Promise<number>} Total relationship count
   */
  private static async countRelationships(): Promise<number> {
    try {
      const relationshipsRef = collection(db, 'relationships');
      const q = query(relationshipsRef, where('isDeleted', '!=', true));
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error: any) {
      console.error('Failed to count relationships:', error);
      return 0;
    }
  }

  /**
   * Count total photos in database
   *
   * @returns {Promise<number>} Total photo count
   */
  private static async countPhotos(): Promise<number> {
    try {
      const photosRef = collection(db, 'photos');
      const q = query(photosRef, where('isDeleted', '!=', true));
      const querySnapshot = await getDocs(q);
      return querySnapshot.size;
    } catch (error: any) {
      console.error('Failed to count photos:', error);
      return 0;
    }
  }

  /**
   * Calculate number of generations in family tree
   * Simple calculation based on birth year ranges
   *
   * @param {Person[]} persons - Array of all persons
   * @returns {number} Estimated number of generations
   */
  private static calculateGenerations(persons: Person[]): number {
    if (persons.length === 0) return 0;

    // Filter persons with birth dates
    const withBirthDates = persons.filter(p => p.birthDate);
    if (withBirthDates.length === 0) return 1;

    // Get min and max birth years
    const birthYears = withBirthDates.map(p => new Date(p.birthDate!).getFullYear());
    const minYear = Math.min(...birthYears);
    const maxYear = Math.max(...birthYears);

    // Estimate generations (assume ~25 years per generation)
    const yearSpan = maxYear - minYear;
    const generations = Math.ceil(yearSpan / 25) + 1;

    return Math.max(1, Math.min(generations, 15)); // Cap at reasonable range
  }

  /**
   * Get human-readable action text from change
   *
   * @param {Change} change - Change object
   * @returns {string} Action text
   */
  private static getActionText(change: Change): string {
    const verb = change.changeType === 'create' ? 'added' :
                 change.changeType === 'update' ? 'updated' :
                 change.changeType === 'delete' ? 'deleted' : 'modified';

    const entityType = change.entityType === 'person' ? 'a person' :
                       change.entityType === 'relationship' ? 'a relationship' :
                       change.entityType === 'photo' ? 'a photo' :
                       change.entityType === 'comment' ? 'a comment' :
                       change.entityType === 'user' ? 'a user' :
                       change.entityType === 'album' ? 'an album' :
                       change.entityType === 'story' ? 'a story' : 'an item';

    return `${verb} ${entityType}`;
  }

  /**
   * Get detailed description from change
   *
   * @param {Change} change - Change object
   * @returns {string} Description text
   */
  private static getActionDescription(change: Change): string {
    // Try to get a meaningful description from the change data
    if (change.afterState) {
      if (change.entityType === 'person') {
        const firstName = change.afterState.firstName || '';
        const lastName = change.afterState.lastName || '';
        return `${firstName} ${lastName}`.trim() || 'Person profile';
      }
    }
    return this.getActionText(change);
  }

  /**
   * Get navigation link for change
   *
   * @param {Change} change - Change object
   * @returns {string | undefined} Link URL
   */
  private static getActionLink(change: Change): string | undefined {
    if (change.entityType === 'person' && change.entityId) {
      return `/people/${change.entityId}`;
    }
    return undefined;
  }

  /**
   * Get icon name for change type
   *
   * @param {Change} change - Change object
   * @returns {string} Icon identifier
   */
  private static getActionIcon(change: Change): string {
    switch (change.entityType) {
      case 'person': return 'person';
      case 'relationship': return 'link';
      case 'lifeEvent': return 'event';
      case 'photo': return 'photo';
      case 'comment': return 'comment';
      case 'album': return 'photo';
      case 'story': return 'book';
      case 'user': return 'person';
      default: return 'edit';
    }
  }
}
